/* eslint-disable import/prefer-default-export, quote-props */

import { SalesRecording, SalesRecordingStatus, SalesRecordingType } from '@marageti/z4-sdk/lib/travel/SalesRecording';
import { TripMatchStatus, TripMatch } from '@marageti/z4-sdk/lib/travel/TripMatch';

type SalesRecordingGate = Pick<SalesRecording, 'status' | 'salesRecordingType'>;

export const isCanceledSale = (status?: TripMatchStatus) => status === TripMatchStatus.MatchSoldCanceled
  || status === TripMatchStatus.MatchRefundedCanceled;

export const monthMap: { [key: string]: string } = {
  '01': 'January',
  '02': 'February',
  '03': 'March',
  '04': 'April',
  '05': 'May',
  '06': 'June',
  '07': 'July',
  '08': 'August',
  '09': 'September',
  '10': 'October',
  '11': 'November',
  '12': 'December',
};

/**
 * Format the invoice date to MMMM YYYY'
 * @param invoiceDate the inovice date in the format of 'YYYY-MM'
 */
export const formatInvoiceMonth = (invoiceDate: string) => {
  if (!invoiceDate) return '-';
  const [year, month] = invoiceDate.split('-');
  return `${monthMap[month]} ${year}`;
};

export const salesRecordingTypeLabel = {
  [SalesRecordingType.Adjustment]: 'Sales Adjustment',
  [SalesRecordingType.General]: 'Standard Sale',
  [SalesRecordingType.PartnerRepeat]: 'Repeat Sale',
  [SalesRecordingType.TravelerReferral]: 'Referral Sale',
  [SalesRecordingType.CommissionAfterTravel]: 'Commission After Travel',
  [SalesRecordingType.PlanningFee]: 'Planning Fee',
  [SalesRecordingType.RefundCredit]: 'Refund Credit',
};

export const shouldShowSalesCard = (tripMatch: TripMatch) => {
  const statuses = [
    TripMatchStatus.MatchActive,
    TripMatchStatus.MatchSold,
    TripMatchStatus.MatchRefundedCanceled,
    TripMatchStatus.MatchSoldCanceled,
  ];
  return statuses.includes(tripMatch.status);
};

/**
 * Whether the partner can submit a (new) Sales Adjustment.
 * Allowed only for SOLD trips once the latest recording is Invoiced (so adjustments can be submitted
 * even after everything has been invoiced). Canceled trips (SOLD_CANCELED and REFUNDED_CANCELED)
 * cannot report a sales adjustment — the Sales Adjustment banner is hidden for both.
 */
export const canAddSalesAdjustment = (tripMatch?: TripMatch, salesRecordings?: SalesRecording[]) => {
  const relevant = salesRecordings?.filter(
    (sale) => sale.salesRecordingType === SalesRecordingType.General
      || sale.salesRecordingType === SalesRecordingType.Adjustment
      || sale.salesRecordingType === SalesRecordingType.CommissionAfterTravel,
  );
  if (!relevant || relevant.length === 0) return false;
  return tripMatch?.status === TripMatchStatus.MatchSold
    && relevant[relevant.length - 1].status === SalesRecordingStatus.InvoicedStatus;
};

/**
 * Whether a sales recording can be edited.
 * Editable only while Open (SOLD), regardless of trip match status. Canceled trips (SOLD_CANCELED /
 * REFUNDED_CANCELED) keep existing Open recordings editable (Standard Sale and Adjustment) even
 * though the Standard Sale can no longer be deleted (see canRemoveSalesRecording), and a new Sales
 * Adjustment can no longer be reported (see canAddSalesAdjustment). Backend also rejects
 * Verified/Invoiced updates for any trip match status.
 */
export const canEditSalesRecording = (
  // Kept for call-site consistency with canRemoveSalesRecording; edit rules are status-only.
  _tripMatchStatus: TripMatchStatus | undefined,
  record: SalesRecordingGate,
) => record.status === SalesRecordingStatus.SoldStatus;

/**
 * Whether a sales recording can be removed.
 * - Standard Sale on a canceled trip (SOLD_CANCELED / REFUNDED_CANCELED): never removable.
 * - Otherwise (including Adjustments on canceled trips): only while Open.
 * Backend rejects Verified/Invoiced deletes and blocks deleting the Standard Sale on canceled TMs.
 */
export const canRemoveSalesRecording = (
  tripMatchStatus: TripMatchStatus | undefined,
  record: SalesRecordingGate,
) => {
  if (isCanceledSale(tripMatchStatus) && record.salesRecordingType === SalesRecordingType.General) {
    return false;
  }
  return record.status === SalesRecordingStatus.SoldStatus;
};
